import React from 'react';
import type { IFileInputValidationErrors } from '../../models/IValidationErrors';
import FileInput, { type IFileInputProps } from './FileInput/FileInput';
import AddedFile, { type IFile, type IAddedFileProps } from './AddedFile/AddedFile';
import styles from './MultifileInput.module.scss';
import { FileExtension } from './models/FileExtension';

export interface IMultifileInputProps extends React.PropsWithChildren {
  onAdd: (file: File) => void;
  onRemove: (key: string) => void;
  acceptedTypes?: FileExtension[];
  addedFiles: IFile[];
  errors?: IFileInputValidationErrors;
  filesLimit?: number;
  helpText?: string;
  children?: React.ReactNode;
  maxFileSizeBytes?: number;
  label: string;
  optionalMessage?: string;
}

export default function MultifileInput({
  onAdd,
  onRemove,
  acceptedTypes = [
    FileExtension.Jpeg,
    FileExtension.Jpg,
    FileExtension.Doc,
    FileExtension.Docx,
    FileExtension.Pdf,
  ],
  addedFiles = [],
  errors,
  filesLimit = 10,
  helpText,
  children,
  label,
  optionalMessage,
}: IMultifileInputProps) {
  const identityDocumentInputId = 'identity-document-input';

  const fileInputProps: IFileInputProps = {
    onChange: onAdd,
    acceptedTypes,
    errors: errors?.inputErrors,
    inputId: identityDocumentInputId,
  };

  const addedFilesProps: IAddedFileProps[] = addedFiles?.map((addedFile: IFile, index: number) => ({
    errors: errors?.filesErrors[index],
    file: addedFile,
    onRemove,
    inputId: identityDocumentInputId,
  }));

  return (
    <div className={styles['input-container']}>
      <label className={`${styles.label}`} htmlFor={identityDocumentInputId}>
        {label}
      </label>
      <div className={`${styles.textxs}`}>
        {helpText}
        {children}
      </div>
      {addedFiles?.length < filesLimit && <FileInput {...fileInputProps} />}
      <div className={`${styles.optionalMessage}`}>{optionalMessage}</div>
      {addedFilesProps.map((props: IAddedFileProps) => (
        <div className={`${styles.addedFile}`} key={props.file.key}>
          <AddedFile {...props} />
        </div>
      ))}
    </div>
  );
}
